import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { AssetId, WorkRequestId } from "../lib/workIds";
import type {
  RequestPriority,
  SelectableWorkRequestDeclineReasonCode,
} from "../types/workRequest";

const workRequestMutationResponseSchema = z.object({
  workRequestId: z.string().uuid(),
});

type CancelWorkRequestInput = {
  requestNumber?: string;
  workRequestId: WorkRequestId;
};

type DeclineWorkRequestInput = {
  reasonCode: SelectableWorkRequestDeclineReasonCode;
  note: string | null;
  duplicateOfWorkRequestId: import("../lib/workIds").WorkRequestId | null;
  requestNumber?: string;
  workRequestId: WorkRequestId;
};

type DeleteWorkRequestInput = {
  requestNumber?: string;
  workRequestId: WorkRequestId;
};

type RestoreWorkRequestInput = {
  requestExternalId: string;
  requestNumber?: string;
};

type UpdateWorkRequestInput = {
  requestNumber?: string;
  workRequestId: WorkRequestId;
  assetId: AssetId | null;
  priorityCode: RequestPriority | null;
  title: string;
  description: string | null;
};

// Readable reasons only: request-validation 400s carry a serialized issue
// list (starting with "[" or "{"), which is not fit to show a user.
const errorResponseSchema = z.object({
  error: z
    .string()
    .min(1)
    .refine((message) => !/^\s*[[{]/.test(message)),
});

/** An update the server refused for a reason retrying won't fix. */
class WorkRequestUpdateRejectedError extends Error {}

function requestError(action: string, status: number) {
  return new Error(`${action}: ${status}`);
}

export function useCancelWorkRequestMutation() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useMutation({
    mutationKey: ["work", "requests", "cancel"],
    mutationFn: async ({ workRequestId }: CancelWorkRequestInput) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }

      // Cancellation has no reason input or stored cancellation reason.
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].requests[":requestId"].cancel.$post({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          requestId: workRequestId,
        },
      });
      if (!response.ok) {
        throw requestError(
          "Failed to cancel the Work Request",
          response.status,
        );
      }
      return workRequestMutationResponseSchema.parse(await response.json());
    },
    onSuccess: async (_data, variables) => {
      toast.add(
        `Work Request ${variables.requestNumber ?? `WR-${variables.workRequestId}`} Canceled.`,
        { icon: true },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
        queryClient.invalidateQueries({ queryKey: ["work-request"] }),
      ]);
    },
    onError: () => {
      toast.error("Unable to cancel the Work Request. Try again.", {
        icon: true,
      });
    },
  });
}

export function useDeclineWorkRequestMutation() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useMutation({
    mutationKey: ["work", "requests", "decline"],
    mutationFn: async ({
      reasonCode,
      note,
      duplicateOfWorkRequestId,
      workRequestId,
    }: DeclineWorkRequestInput) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].requests[":requestId"].decline.$post({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          requestId: workRequestId,
        },
        json: { reasonCode, note, duplicateOfWorkRequestId },
      });
      if (!response.ok) {
        throw requestError(
          "Failed to decline the Work Request",
          response.status,
        );
      }
      return workRequestMutationResponseSchema.parse(await response.json());
    },
    onSuccess: async (_data, variables) => {
      toast.add(
        `Work Request ${variables.requestNumber ?? `WR-${variables.workRequestId}`} declined.`,
        { icon: true },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
        queryClient.invalidateQueries({ queryKey: ["work-request"] }),
      ]);
    },
    onError: () => {
      toast.error("Unable to decline the Work Request. Try again.", {
        icon: true,
      });
    },
  });
}
export function useDeleteWorkRequestMutation() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useMutation({
    mutationKey: ["work", "requests", "delete"],
    mutationFn: async ({ workRequestId }: DeleteWorkRequestInput) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].requests[":requestId"].$delete({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          requestId: workRequestId,
        },
      });
      if (!response.ok) {
        throw requestError(
          "Failed to delete the Work Request",
          response.status,
        );
      }
      return workRequestMutationResponseSchema.parse(await response.json());
    },
    onSuccess: async (_data, variables) => {
      toast.add(
        `Work Request ${variables.requestNumber ?? `WR-${variables.workRequestId}`} deleted.`,
        { icon: true },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
        queryClient.invalidateQueries({ queryKey: ["work-request"] }),
      ]);
    },
    onError: () => {
      toast.error("Unable to delete the Work Request. Try again.", {
        icon: true,
      });
    },
  });
}

export function useRestoreWorkRequestMutation() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useMutation({
    mutationKey: ["work", "requests", "restore"],
    mutationFn: async ({ requestExternalId }: RestoreWorkRequestInput) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].requests[":requestExternalId"].restore.$post({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          requestExternalId,
        },
      });
      if (!response.ok) {
        throw requestError(
          "Failed to restore the Work Request",
          response.status,
        );
      }
      return workRequestMutationResponseSchema.parse(await response.json());
    },
    onSuccess: async (_data, variables) => {
      toast.add(
        `Work Request ${variables.requestNumber ?? variables.requestExternalId} restored.`,
        { icon: true },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
        queryClient.invalidateQueries({ queryKey: ["work-request"] }),
      ]);
    },
    onError: () => {
      toast.error("Unable to restore the Work Request. Try again.", {
        icon: true,
      });
    },
  });
}
export function useUpdateWorkRequestMutation() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useMutation({
    mutationKey: ["work", "requests", "update"],
    mutationFn: async ({
      requestNumber: _requestNumber,
      workRequestId,
      ...input
    }: UpdateWorkRequestInput) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }

      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].requests[":requestId"].$patch({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          requestId: workRequestId,
        },
        json: input,
      });
      if (!response.ok) {
        // A 400 carries a user-facing reason (for example, an asset that is
        // no longer available), so show it rather than the bare status.
        const badRequestMessage =
          response.status === 400
            ? errorResponseSchema.safeParse(
                await response.json().catch(() => null),
              ).data?.error
            : undefined;
        throw badRequestMessage
          ? new WorkRequestUpdateRejectedError(badRequestMessage)
          : requestError("Failed to update the Work Request", response.status);
      }
      return workRequestMutationResponseSchema.parse(await response.json());
    },
    onSuccess: async (_data, variables) => {
      toast.add(
        `Work Request ${variables.requestNumber ?? `WR-${variables.workRequestId}`} updated.`,
        { icon: true },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
        queryClient.invalidateQueries({ queryKey: ["work-request"] }),
      ]);
    },
    onError: (error) => {
      toast.error(
        error instanceof WorkRequestUpdateRejectedError
          ? error.message
          : "Unable to update the Work Request. Try again.",
        { icon: true },
      );
    },
  });
}
